import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { DashboardSkeleton, ProfileSkeleton, GallerySkeleton } from '@/components/ContentSkeleton';

// Lazy load page components
export const LazyDashboard = lazy(() => import('@/pages/Dashboard'));
export const LazyProfile = lazy(() => import('@/pages/Profile'));
export const LazyGallery = lazy(() => import('@/pages/Gallery'));
export const LazyGenerate = lazy(() => import('@/pages/Generate'));
export const LazyECard = lazy(() => import('@/pages/ECard'));
export const LazyResults = lazy(() => import('@/pages/Results'));

// Lazy load home page sections
export const LazyHomeGenerator = lazy(() => import('@/components/HomeGenerator'));
export const LazyHomeGallery = lazy(() => import('@/components/HomeGallery'));
export const LazyHomeThemes = lazy(() => import('@/components/HomeThemes'));
export const LazyComingSoon = lazy(() => import('@/components/ComingSoon'));
export const LazyPricing = lazy(() => import('@/components/Pricing'));

// Lazy load feature components
export const LazyGalleryView = lazy(() => import('@/features/gallery/GalleryView'));
export const LazyProfileView = lazy(() => import('@/features/profile/ProfileView'));

// Custom hook for intersection observer-based lazy loading
export const useInView = (options = {}) => {
  const [isInView, setIsInView] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || hasLoaded) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          setHasLoaded(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px',
        ...options,
      }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [hasLoaded, options]);

  return { ref, isInView: isInView || hasLoaded };
};

// HOC for wrapping lazy components with suspense and appropriate skeletons
export const withSuspense = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
) => {
  return (props: P) => (
    <Suspense fallback={fallback || <Skeleton className="h-96 w-full" />}>
      <Component {...props} />
    </Suspense>
  );
};

// HOC for intersection observer-based lazy loading
export const withLazyLoad = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
) => {
  return (props: P) => {
    const { ref, isInView } = useInView();
    
    return (
      <div ref={ref} className="min-h-[200px]">
        {isInView ? (
          <Suspense fallback={fallback || <Skeleton className="h-96 w-full animate-pulse" />}>
            <Component {...props} />
          </Suspense>
        ) : (
          fallback || <Skeleton className="h-96 w-full animate-pulse" />
        )}
      </div>
    );
  };
};

// Pre-configured lazy components with appropriate skeletons
export const SuspenseDashboard = withSuspense(LazyDashboard, <DashboardSkeleton />);
export const SuspenseProfile = withSuspense(LazyProfile, <ProfileSkeleton />);
export const SuspenseGallery = withSuspense(LazyGallery, <GallerySkeleton />);
export const SuspenseGenerate = withSuspense(LazyGenerate, <Skeleton className="h-96 w-full" />);
export const SuspenseECard = withSuspense(LazyECard, <Skeleton className="h-96 w-full" />);
export const SuspenseResults = withSuspense(LazyResults, <Skeleton className="h-96 w-full" />);

// Lazy-loaded home page sections with intersection observer
export const LazyLoadHomeGenerator = withLazyLoad(
  LazyHomeGenerator,
  <div className="py-20 px-4">
    <div className="container mx-auto">
      <div className="text-center mb-12">
        <Skeleton className="h-12 w-96 mx-auto mb-4" />
        <Skeleton className="h-6 w-128 mx-auto" />
      </div>
      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Skeleton className="h-96 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
      <div className="text-center mt-8">
        <Skeleton className="h-12 w-48 mx-auto" />
      </div>
    </div>
  </div>
);

export const LazyLoadHomeGallery = withLazyLoad(
  LazyHomeGallery,
  <div className="py-20 px-4">
    <div className="container mx-auto">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-48 mx-auto mb-4" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-4 w-32" />
              <div className="flex justify-between items-center">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-8 w-20" />
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Skeleton className="h-10 w-32 mx-auto" />
        </div>
      </div>
    </div>
  </div>
);

export const LazyLoadHomeThemes = withLazyLoad(
  LazyHomeThemes,
  <div className="py-20 px-4 bg-gradient-subtle">
    <div className="container mx-auto">
      <div className="text-center mb-12">
        <Skeleton className="h-10 w-80 mx-auto mb-4" />
        <Skeleton className="h-6 w-96 mx-auto" />
      </div>
      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
        {[1, 2, 3].map((i) => (
          <div key={i} className="overflow-hidden border rounded-lg">
            <Skeleton className="h-64 w-full" />
            <div className="p-6">
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        ))}
      </div>
      <div className="text-center">
        <Skeleton className="h-10 w-40 mx-auto" />
      </div>
    </div>
  </div>
);

export const LazyLoadComingSoon = withLazyLoad(
  LazyComingSoon,
  <div className="py-20 px-4 bg-gradient-subtle">
    <div className="container mx-auto">
      <div className="mb-20">
        <div className="rounded-3xl p-12 md:p-16 text-center">
          <Skeleton className="h-16 w-16 mx-auto mb-6 rounded-full" />
          <Skeleton className="h-12 w-96 mx-auto mb-6" />
          <Skeleton className="h-6 w-full max-w-3xl mx-auto mb-8" />
          <div className="flex gap-4 justify-center">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
      </div>
      <div className="text-center mb-12">
        <Skeleton className="h-10 w-80 mx-auto mb-4" />
        <Skeleton className="h-6 w-96 mx-auto" />
      </div>
      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-8">
              <Skeleton className="h-16 w-16 mx-auto mb-6 rounded-full" />
              <Skeleton className="h-6 w-32 mx-auto mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4 mx-auto" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </div>
);

export const LazyLoadPricing = withLazyLoad(
  LazyPricing,
  <div className="py-20 px-4">
    <div className="container mx-auto">
      <div className="text-center mb-12">
        <Skeleton className="h-12 w-64 mx-auto mb-4" />
        <Skeleton className="h-6 w-96 mx-auto" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-96 w-full" />
        ))}
      </div>
    </div>
  </div>
);